import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from "framer-motion";
import { ChevronLeft, Plane, Briefcase, MessageCircle, GraduationCap, Film, Clock, Zap } from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import LanguageOnboardingStep from "@/components/language/LanguageOnboardingStep";
import AvatarTeacher from "@/components/language/AvatarTeacher";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useLanguageLearning, type LangCode } from "@/hooks/useLanguageLearning";
import { useXP } from "@/hooks/useXP";
import { LanguageState } from "@/components/language/LanguageState";
import LanguagePendingRecoveryPanel from "@/components/language/LanguagePendingRecoveryPanel";
import { toast } from "sonner";

const TOTAL_STEPS = 3;

const LanguageOnboardingContent: React.FC<{ learning: ReturnType<typeof useLanguageLearning> }> = ({ learning }) => {
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedLang = searchParams.get("lang");
  const preselectedLang = ["en", "pt", "nl", "es", "fr", "de", "it", "ja"].includes(requestedLang || "") ? requestedLang : null;
  const { t, lang } = useLanguage();
  const { upsertProfile, addXpToday } = learning;
  const nativeCode = lang.toLowerCase().split("-")[0];
  const nativeLanguage = ["en", "pt", "nl", "es", "fr", "de", "it", "ja"].includes(nativeCode) ? nativeCode as LangCode : "en";
  const { addXP } = useXP();
  const [step, setStep] = useState(preselectedLang ? 2 : 1);
  const [selectedLang, setSelectedLang] = useState<string[]>(preselectedLang ? [preselectedLang] : []);
  const [selectedLevel, setSelectedLevel] = useState<string[]>([]);

  const [selectedTime, setSelectedTime] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [showComplete, setShowComplete] = useState(false), [saveError, setSaveError] = useState(false), [awardedXP, setAwardedXP] = useState(0);
  const busy = useRef(false), mounted = useRef(false), redirect = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; clearTimeout(redirect.current); }; }, []);

  /* ─── OPTIONS ─── */
  const langOptions = [
    { id: "en", label: "English", emoji: "🇬🇧" },
    { id: "pt", label: "Português", emoji: "🇧🇷" },
    { id: "nl", label: "Nederlands", emoji: "🇳🇱" },
    { id: "es", label: "Español", emoji: "🇪🇸" },
    { id: "fr", label: "Français", emoji: "🇫🇷" },
    { id: "de", label: "Deutsch", emoji: "🇩🇪" },
    { id: "it", label: "Italiano", emoji: "🇮🇹" },
    { id: "ja", label: "日本語", emoji: "🇯🇵" },
  ];

  const levelOptions = [
    { id: "complete_beginner", label: t("language.setup.level.completeBeginner"), description: t("language.setup.level.completeBeginner.desc") },
    { id: "beginner", label: t("language.setup.level.beginner"), description: t("language.setup.level.beginner.desc") },
    { id: "intermediate", label: t("language.setup.level.intermediate"), description: t("language.setup.level.intermediate.desc") },
    { id: "advanced", label: t("language.setup.level.advanced"), description: t("language.setup.level.advanced.desc") },
  ];

  const timeOptions = [
    { id: "5", label: t("language.setup.time.5"), icon: Clock },
    { id: "10", label: t("language.setup.time.10"), icon: Clock },
    { id: "15", label: t("language.setup.time.15"), icon: Clock },
    { id: "30", label: t("language.setup.time.30"), icon: Zap },
  ];

  /* ─── SELECTION HANDLERS ─── */
  const handleSelectLang = useCallback((id: string) => setSelectedLang([id]), []);
  const handleSelectLevel = useCallback((id: string) => setSelectedLevel([id]), []);
  const handleSelectTime = useCallback((id: string) => setSelectedTime([id]), []);

  /* ─── NAVIGATION ─── */
  const canContinue = () => {
    if (step === 1) return selectedLang.length > 0;
    if (step === 2) return selectedLevel.length > 0;
    if (step === 3) return selectedTime.length > 0;
    return false;
  };

  const stepRef = useRef(step); stepRef.current = step;
  const handleNext = useCallback(async () => {
    if (busy.current || !canContinue() || stepRef.current !== step) return;
    if (step < TOTAL_STEPS) { stepRef.current = step + 1; setStep(step + 1); return; }
    const current = learning.capture(); busy.current = true; setSaving(true); setSaveError(false);
    try {
      const level = selectedLevel[0] === "complete_beginner" ? "beginner" : selectedLevel[0] as "beginner" | "intermediate" | "advanced";
      const wasNew = !learning.profiles.some(p => p.learning_language === selectedLang[0]);
      const result = await upsertProfile({ learning_language: selectedLang[0] as LangCode, native_language: nativeLanguage, level, daily_goal_minutes: Number(selectedTime[0]) });
      if (!mounted.current || !current()) return;
      if (!result) { setSaveError(true); return; }
      setShowComplete(true);
      if (wasNew) void addXP("language", "onboarding_complete", 25).then(async reward => { if (!mounted.current || !current() || !reward.inserted) return; setAwardedXP(25); await addXpToday(25); }).catch(() => {});
      redirect.current = setTimeout(() => { if (mounted.current && current()) navigate("/language"); }, 2000);
    } catch { if (mounted.current && current()) setSaveError(true); }
    finally { busy.current = false; if (mounted.current) setSaving(false); }
  }, [step, selectedLang, selectedLevel, selectedTime, lang, nativeLanguage, learning, upsertProfile, addXpToday, addXP, navigate]);

  const handleBack = useCallback(() => {
    if (busy.current) return;
    if (step > 1) setStep(s => s - 1);
    else navigate(-1);
  }, [step, navigate]);

  /* ─── COMPLETION SCREEN ─── */
  if (showComplete) {
    return (
      <MobileLayout className="language-page flex flex-col items-center justify-center px-6" style={{ background: "#0a0a0f" }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <AvatarTeacher />
          <motion.p
            className="text-[14px] font-body text-foreground/60 text-center mt-5 italic"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            {t("language.setup.preparing")}
          </motion.p>
          <motion.div
            className="flex justify-center mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          </motion.div>
        </motion.div>
      </MobileLayout>
    );
  }

  /* ─── STEP CONFIG ─── */
  const stepConfig: Record<number, {
    title: string;
    subtitle?: string;
    options: any[];
    multiSelect: boolean;
    selected: string[];
    onSelect: (id: string) => void;
  }> = {
    1: { title: t("language.setup.step1.title"), subtitle: t("language.setup.step1.subtitle"), options: langOptions, multiSelect: false, selected: selectedLang, onSelect: handleSelectLang },
    2: { title: t("language.setup.step2.title"), subtitle: t("language.setup.step2.subtitle"), options: levelOptions, multiSelect: false, selected: selectedLevel, onSelect: handleSelectLevel },
    3: { title: t("language.setup.step4.title"), subtitle: t("language.setup.step4.subtitle"), options: timeOptions, multiSelect: false, selected: selectedTime, onSelect: handleSelectTime },
  };

  const current = stepConfig[step];

  return (
    <MobileLayout className="language-page pb-8 pt-6 flex flex-col min-h-screen-safe" noPadding>
      <div className="px-5 flex-1 flex flex-col">
        <LanguagePendingRecoveryPanel refreshKey="language-onboarding" />
        {/* Back button */}
        <motion.button
          disabled={saving} onClick={handleBack}
          className="flex items-center gap-1 text-muted-foreground mb-4 active:opacity-70 self-start"
          initial={{ opacity: 0 }}
          animate={{ opacity: step > 1 ? 1 : 0.4 }}
        >
          <ChevronLeft size={16} />
          <span className="text-[13px] font-display">{t("language.setup.back")}</span>
        </motion.button>

        {/* Step content */}
        <div className="flex-1 flex flex-col justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.25 }}
            >
              <LanguageOnboardingStep
                stepNumber={step}
                totalSteps={TOTAL_STEPS}
                title={current.title}
                subtitle={current.subtitle}
                options={current.options}
                multiSelect={current.multiSelect}
                selectedIds={current.selected}
                onSelect={value => { if (!busy.current) current.onSelect(value); }}
              />
            </motion.div>
          </AnimatePresence>
        </div>

        {step === TOTAL_STEPS && canContinue() && <section aria-label={t('languageAction.review')} className="min-w-0 space-y-2 rounded-xl border border-border p-3">
          <p>{t('languageAction.field.language')}: {t('languageAction.language.' + selectedLang[0])}</p>
          <p>{t('languageAction.field.native_language')}: {t('languageAction.language.' + nativeLanguage)}</p>
          <p>{t('languageAction.field.level')}: {t('languageAction.level.' + (selectedLevel[0] === 'complete_beginner' ? 'beginner' : selectedLevel[0]))}</p>
          <p>{t('languageAction.field.daily_goal_minutes')}: {selectedTime[0]}</p>
          <p className="text-sm text-muted-foreground">{t('languageAction.confirmAll')}</p>
        </section>}
        {saveError && <p role="alert" className="language-status">{t("language.ux.saveError")}</p>}
        {/* Continue button */}
        <div className="pt-4 pb-2">
          <motion.button
            onClick={handleNext}
            disabled={!canContinue() || saving}
            className="w-full py-4 rounded-2xl font-display text-[15px] font-bold tracking-wide transition-all active:scale-[0.97]"
            style={{
              background: canContinue() ? "hsl(187 72% 50%)" : "hsl(187 72% 50% / 0.15)",
              color: canContinue() ? "#0a0a0f" : "hsl(218 14% 40%)",
              boxShadow: canContinue() ? "0 0 25px hsl(187 72% 50% / 0.2)" : "none",
            }}
            animate={{ opacity: canContinue() ? 1 : 0.6 }}
          >
            {saving ? (
              <div className="w-5 h-5 border-2 border-background/30 border-t-background rounded-full animate-spin mx-auto" />
            ) : (
              t(step === TOTAL_STEPS ? "common.confirm" : "language.setup.next")
            )}
          </motion.button>
        </div>
      </div>
    </MobileLayout>
  );
};

const LanguageOnboarding: React.FC = () => {
  const learning = useLanguageLearning();
  if (!learning.userId || !learning.hasData) return <LanguageState learning={learning} />;
  return <MotionConfig reducedMotion="user"><LanguageOnboardingContent key={learning.userId} learning={learning} /></MotionConfig>;
};
export default LanguageOnboarding;
